/**
 * InsCoder WANG.J 2026-08-13 B + Testimonials masonry: equal-height cards & full-text modal
 * - Unifies every card to the TALLEST item's height, exposed as the `--testimonial-height`
 *   CSS variable on each `.f-column` item (applied in section-testimonials-masonry.css).
 * - Clamps every `.testimonial__content` to the line count of the SHORTEST text via the
 *   `--limit-line-size` CSS variable, so all cards share the same amount of text.
 * - Every `.testimonial__content` is clickable and opens the theme's `<basic-modal>` with the full text.
 */
(function () {
  'use strict';

  var SECTION_SELECTOR = '.testimonials-masonry';
  var BOUND_ATTR = 'data-line-clamp-bound';
  var LINE_VAR = '--limit-line-size';
  var HEIGHT_VAR = '--testimonial-height';

  function debounce(fn, wait) {
    var timer;
    return function () {
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function getModal(item) {
    var modalId = item.getAttribute('data-modal-id');
    if (!modalId) return null;
    var modal = document.getElementById(modalId);
    return modal && typeof modal.show === 'function' ? modal : null;
  }

  // Cards sit in multiple columns when the second card starts on the same row as the first
  // (vertical overlap). On mobile the grid collapses to a single column, so we skip clamping.
  function isMultiColumn(items) {
    if (items.length < 2) return false;
    var first = items[0].getBoundingClientRect();
    var second = items[1].getBoundingClientRect();
    return second.top < first.bottom;
  }

  function resetCards(items) {
    items.forEach(function (item) {
      item.style.removeProperty(HEIGHT_VAR);
      var content = item.querySelector('.testimonial__content');
      if (!content) return;
      content.style.removeProperty(LINE_VAR);
    });
  }

  function wireModalTriggers(items) {
    items.forEach(function (item) {
      var content = item.querySelector('.testimonial__content');
      if (!content) return;

      var modal = getModal(item);
      if (!modal) return;

      content.setAttribute('tabindex', '0');
      content.setAttribute('aria-haspopup', 'dialog');
      content.setAttribute('aria-expanded', 'false');

      if (content.dataset.modalBound === 'true') return;
      content.dataset.modalBound = 'true';

      content.addEventListener('click', function (event) {
        if (event.target.closest('a, button, [aria-controls]')) return;
        event.preventDefault();
        modal.show(content);
      });

      content.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        modal.show(content);
      });

      modal.addEventListener('toggle', function () {
        content.setAttribute('aria-expanded', modal.open ? 'true' : 'false');
      });
    });
  }

  function applyLineClamp(wrapper, items) {
    if (!isMultiColumn(items)) {
      resetCards(items);
      wireModalTriggers(items);
      return;
    }

    resetCards(items);
    // Force a reflow so measurements reflect the un-clamped state.
    void wrapper.offsetHeight;

    // 1) Temporarily lift the clamp to measure each text's natural line count,
    //    then unify every card to the line count of the shortest text.
    items.forEach(function (item) {
      var content = item.querySelector('.testimonial__content');
      if (!content) return;
      content.style.setProperty(LINE_VAR, '999');
    });
    void wrapper.offsetHeight;

    var shortestLines = Infinity;
    items.forEach(function (item) {
      var content = item.querySelector('.testimonial__content');
      if (!content) return;
      var lineHeight = parseFloat(window.getComputedStyle(content).lineHeight) || 24;
      var lines = Math.ceil(content.getBoundingClientRect().height / lineHeight);
      if (lines > 0 && lines < shortestLines) shortestLines = lines;
    });

    if (shortestLines === Infinity) {
      items.forEach(function (item) {
        var content = item.querySelector('.testimonial__content');
        if (content) content.style.removeProperty(LINE_VAR);
      });
    } else {
      items.forEach(function (item) {
        var content = item.querySelector('.testimonial__content');
        if (!content) return;
        content.style.setProperty(LINE_VAR, shortestLines);
      });
    }

    // 2) Unify the card heights to the tallest item via a CSS variable.
    void wrapper.offsetHeight;
    var maxHeight = 0;
    items.forEach(function (item) {
      var height = item.getBoundingClientRect().height;
      if (height > maxHeight) maxHeight = height;
    });
    if (maxHeight > 0) {
      items.forEach(function (item) {
        item.style.setProperty(HEIGHT_VAR, maxHeight + 'px');
      });
    }

    wireModalTriggers(items);
  }

  function initSection(section) {
    var wrapper = section.querySelector('.testimonials-masonry__items');
    if (!wrapper || wrapper.getAttribute(BOUND_ATTR) === 'true') return;
    wrapper.setAttribute(BOUND_ATTR, 'true');

    var items = Array.from(wrapper.querySelectorAll('.f-column'));
    if (items.length < 2) return;

    var apply = debounce(function () {
      applyLineClamp(wrapper, items);
    }, 80);

    var initialApply = function () {
      apply();
      if (document.fonts) document.fonts.ready.then(apply);
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialApply);
    } else {
      initialApply();
    }
    window.addEventListener('load', apply);
    window.addEventListener('resize', apply);

    if (window.ResizeObserver) {
      var observer = new ResizeObserver(apply);
      observer.observe(wrapper);
      items.forEach(function (item) {
        observer.observe(item);
      });
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
  document.addEventListener('shopify:block:load', function (event) {
    initAll(event.target);
  });
})();
/** InsCoder WANG.J 2026-08-13 E + Testimonials masonry: equal-height cards & full-text modal **/
